import { useEffect, useMemo, useState } from 'react'
import {
  Line,
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { format, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import { Card, Button, Title, Text, Icon } from '@nimbus-ds/components'
import { CashIcon } from '@nimbus-ds/icons'
import { funnelService, type ProductsOriginsTimelineResponse } from '@/api/funnel'
import { formatCurrency } from '@/lib/utils'

// P3 — per-day revenue trend. Single series over the window, toggleable between total web
// revenue, AOV (revenue÷conversions), and revenue-per-session (revenue÷sessions). Reads the
// per-day `revenue`/`conversions`/`total_sessions` the products_origins timeline now emits;
// 0/absent on snapshots predating the field (no reconstruction).

type Metric = 'revenue' | 'aov' | 'rps'
type Smoothing = 'raw' | 'ma3'

const METRIC_LABELS: Record<Metric, string> = {
  revenue: 'Ingresos',
  aov: 'Ticket prom.',
  rps: 'Ingreso/sesión',
}
const SMOOTHING_LABELS: Record<Smoothing, string> = { raw: 'Diario', ma3: '3d avg' }

interface RevenueTrendChartProps {
  storeId: string
  windowDays?: number
  initialData?: ProductsOriginsTimelineResponse | null
}

export function RevenueTrendChart({ storeId, windowDays = 7, initialData }: RevenueTrendChartProps) {
  const [data, setData] = useState<ProductsOriginsTimelineResponse | null>(initialData ?? null)
  const [isLoading, setIsLoading] = useState(!initialData)
  const [error, setError] = useState<string | null>(null)
  const [metric, setMetric] = useState<Metric>('revenue')
  const [smoothing, setSmoothing] = useState<Smoothing>('raw')

  useEffect(() => {
    if (initialData) {
      setData(initialData)
      setIsLoading(false)
      return
    }
    if (!storeId) return
    let cancelled = false
    setIsLoading(true)
    setError(null)
    funnelService
      .getStoreProductsOriginsTimeline(storeId, windowDays)
      .then((res) => { if (!cancelled) setData(res) })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load revenue trend:', err)
        setError('No se pudo cargar la evolución de ingresos')
      })
      .finally(() => { if (!cancelled) setIsLoading(false) })
    return () => { cancelled = true }
  }, [storeId, windowDays, initialData])

  const { chartData, hasRevenue } = useMemo(() => {
    if (!data) return { chartData: [], hasRevenue: false }

    type Row = { date: string; label: string; value: number | null }
    const value = (rev: number, conv: number, sess: number): number | null => {
      if (metric === 'revenue') return rev
      // AOV/RPS are undefined without a denominator — null so the line breaks rather than reading 0.
      if (metric === 'aov') return conv > 0 ? +(rev / conv).toFixed(2) : null
      return sess > 0 ? +(rev / sess).toFixed(2) : null
    }

    const points: Row[] = (data.series || []).map((p) => ({
      date: p.date,
      label: format(parseISO(p.date), 'dd MMM', { locale: es }),
      value: value(p.revenue || 0, p.conversions || 0, p.total_sessions || 0),
    }))

    let series = points
    if (smoothing === 'ma3' && points.length) {
      const w = 3
      series = points.map((_, i) => {
        let sum = 0, count = 0
        for (let k = Math.max(0, i - w + 1); k <= i; k++) {
          const v = points[k].value
          if (typeof v === 'number') { sum += v; count += 1 }
        }
        return { date: points[i].date, label: points[i].label, value: count ? Math.round((sum / count) * 100) / 100 : null }
      })
    }

    const hasRev = (data.series || []).some((p) => (p.revenue || 0) > 0)
    return { chartData: series, hasRevenue: hasRev }
  }, [data, metric, smoothing])

  const tickFormatter = (v: number) => {
    if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`
    if (v >= 1_000) return `$${Math.round(v / 1_000)}k`
    return `$${v}`
  }

  const header = (
    <Card.Header>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Icon source={<CashIcon />} />
            <Title as="h3">Evolución de Ingresos</Title>
          </div>
          <Text>Ingresos web diarios · últimos {windowDays} días</Text>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1 rounded-md border p-0.5">
            {(Object.keys(SMOOTHING_LABELS) as Smoothing[]).map((s) => (
              <Button key={s} size="small" appearance={smoothing === s ? 'primary' : 'transparent'}
                onClick={() => setSmoothing(s)}>
                {SMOOTHING_LABELS[s]}
              </Button>
            ))}
          </div>
          <div className="flex gap-1 rounded-md border p-0.5">
            {(Object.keys(METRIC_LABELS) as Metric[]).map((m) => (
              <Button key={m} size="small" appearance={metric === m ? 'primary' : 'transparent'}
                onClick={() => setMetric(m)}>
                {METRIC_LABELS[m]}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </Card.Header>
  )

  if (isLoading) {
    return (
      <Card>
        {header}
        <Card.Body><div className="h-[280px] w-full animate-pulse rounded-md bg-muted" /></Card.Body>
      </Card>
    )
  }
  if (error) {
    return (
      <Card>{header}<Card.Body><p className="text-sm text-red-600">{error}</p></Card.Body></Card>
    )
  }
  if (!chartData.length || !hasRevenue) {
    return (
      <Card>
        {header}
        <Card.Body>
          <Text className="py-8 text-center">Aún no hay ingresos diarios suficientes.</Text>
        </Card.Body>
      </Card>
    )
  }

  return (
    <Card>
      {header}
      <Card.Body>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} tickFormatter={tickFormatter} />
            <Tooltip formatter={(v: number) => [formatCurrency(v), METRIC_LABELS[metric]]} />
            <Line type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2}
              dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls={false} />
          </LineChart>
        </ResponsiveContainer>
      </Card.Body>
    </Card>
  )
}

export default RevenueTrendChart
